import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FilterChipDef } from "./filterChipDefs";
import { useFilterDisplayLabel } from "../../hooks/useFilterDisplayLabel";

interface SearchBarSuggestionsProps {
  readonly matches: FilterChipDef[];
  readonly highlightedIndex: number;
  readonly customerId?: string;
  readonly onPick: (chipDef: FilterChipDef) => void;
  readonly idPrefix?: string;
}

export default function SearchBarSuggestions({
  matches,
  highlightedIndex,
  customerId,
  onPick,
  idPrefix,
}: SearchBarSuggestionsProps) {
  const { t } = useTranslation("search");
  const getDisplayLabel = useFilterDisplayLabel(customerId);

  function popoverHint(popoverType: FilterChipDef["popoverType"]): string {
    switch (popoverType) {
      case "typeahead": return t("searchBar.hint.typeahead", "opens typeahead");
      case "date": return t("searchBar.hint.date", "pick range");
      case "select": return t("searchBar.hint.select", "pick one");
      case "site": return t("searchBar.hint.site", "pick sites");
      case "tags": return t("searchBar.hint.tags", "pick tags");
      case "boolean": return t("searchBar.hint.boolean", "pick value");
      case "duration": return t("searchBar.hint.duration", "set range");
      case "retention": return t("searchBar.hint.retention", "set range");
      default: return t("searchBar.hint.text", "type a value");
    }
  }

  if (matches.length === 0) {
    return (
      <Box
        sx={{
          p: 1.25,
          fontSize: "0.8125rem",
          color: "var(--text-tertiary)",
        }}
      >
        {t("searchBar.noMatch", "No matching filter — keep typing or press Esc")}
      </Box>
    );
  }

  return (
    <Box
      role="listbox"
      sx={{
        p: 0.5,
        maxHeight: 320,
        overflowY: "auto",
        fontSize: "0.8125rem",
      }}
    >
      {matches.map((chipDef, idx) => {
        const displayLabel = getDisplayLabel(chipDef);
        return (
          <Box
            key={chipDef.id}
            id={`${idPrefix ?? "searchbar"}-suggestion-${chipDef.id}`}
            role="option"
            aria-selected={idx === highlightedIndex}
            onMouseDown={(e) => {
              // mousedown so we fire before input blur
              e.preventDefault();
              onPick(chipDef);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: "12px",
              py: "8px",
              borderRadius: "4px",
              cursor: "pointer",
              backgroundColor:
                idx === highlightedIndex ? "var(--bg-hover)" : "transparent",
              "&:hover": { backgroundColor: "var(--bg-hover)" },
            }}
          >
            <Box component="span" sx={{ color: "var(--text-primary)", fontWeight: 600 }}>
              {displayLabel}:
            </Box>
            <Box component="span" sx={{ color: "var(--text-tertiary)", fontSize: "0.75rem", ml: 1 }}>
              {popoverHint(chipDef.popoverType)}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
